import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { errorToast } from 'configuration/notifications';
import { getUnixTimestamp } from 'helpers/utils';

import { stockSelector, stockListSelector } from 'store/stock/selectors';
import { reserveSelector } from 'store/transferAct/selectors';
import { Reservation } from 'store/transferAct/actions';
import { Stock } from 'store/stock/actions';

import { DatePicker } from 'components/DatePicker';
import { ActionButton } from 'components/ActionButton';
import { ModalAttention } from 'components/ModalAttention';

import { ModalClient } from 'components/ModalClient';
import { ModalInstruments } from 'components/ModalInstruments';

const fridayWeekday = 5;
const mondayWeekday = 1;
const DEFFER_RESERVE_TIME_DIFFERENCE = 86400 * 3;
const [IS_APPROVED_REJECTED, IS_APPROVED_REJECTED_VIBER, IS_APPROVED_EXPIRED] = [-1, -2, 2];

const ActionButtonsSection = () => {
  const dispatch = useDispatch();
  const reserve = useSelector(reserveSelector);
  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const [deffererTime, setDeffererTime] = useState();
  const [modalDeffererTime, setModalDeffererTime] = useState(false);

  const [client, setClient] = useState({ open: false, key: 'client', data: {} });
  const [instruments, setInstruments] = useState({ open: false, key: 'instruments', data: [] });
  const [instrumentInModal, setInstrumentInModal] = useState(null);
  const [comment, setComment] = useState({ open: false, key: 'comment', data: '' });
  const [bonus, setBonus] = useState({ open: false, key: 'bonus', data: { comment: '', value: 0 } });
  const [timer, setTimer] = useState({ open: false, key: 'time', data: getUnixTimestamp() });

  useEffect(() => {
    if (reserve) {
      setClient({ ...client, data: reserve.client });
      setInstruments({ ...instruments, data: reserve.instruments });
      setComment({ ...comment, data: reserve.comment || '' });
      setTimer({ ...timer, data: reserve.time || getUnixTimestamp() });
      setBonus({
        ...bonus,
        data: {
          comment: reserve?.bonus?.comment || '',
          value: reserve?.bonus?.value || 0,
        },
      });
    }
  }, [reserve]);

  const handleCloseButton = () => {
    setComment({ ...comment, open: false });
    setBonus({ ...bonus, open: false });
    setTimer({ ...timer, open: false });
  };

  const onCloseModal = () => {
    setInstrumentInModal(null);
  };

  const onCloseDeffererTimeModal = () => {
    setModalDeffererTime(false);
    setDeffererTime();
    handleCloseButton();
  };

  const onSubmitModal = () => {
    handleChange({ key: 'instruments', data: instrumentInModal });
    setInstrumentInModal(null);
  };

  const onSubmitDeffererTimeModal = () => {
    dispatch(Reservation.updateReserve({ time: deffererTime, isDeferrer: true }));
    onCloseDeffererTimeModal();
  };

  const addInstrumentValue = (value) => {
    if (instruments.data.find((elem) => elem.id == value.id)) {
      return;
    }
    if (value?.quantity_inreserve_service > 0) {
      setInstrumentInModal(value);
      return;
    }
    handleChange({ key: 'instruments', data: value });
  };

  const setClientValueHandled = (value) => {
    const newClient = { ...client, open: false, data: value };
    setClient(newClient);
    handleChange(newClient);
  };

  const handleChange = (target) => {
    const newReserve = {};

    switch (target.key) {
      case 'client':
        newReserve.client = target.data;
        break;

      case 'instruments':
        if (!reserve.instruments.find((inst) => inst.id === target.data.id)) {
          if (['Послуга', 'Послуги'].includes(target.data.subtype)) {
            errorToast('В резервациях нельзя выбирать услуги!');
            return;
          }

          if (target.data.stockID !== stock.stockID1C && target.data.stockID !== '') {
            if (reserve.instruments.length === 0 || stock.stockName === 'Доставка') {
              const newStock = stockList.find((stock) => stock.stockID1C === target.data.stockID);

              if (newStock) {
                dispatch(Stock.setStock(newStock));
                newReserve.stockID = newStock.stockID1C;
              }
            } else {
              errorToast('Нельзя брать инструменты из разных складов!');
              return;
            }
          }

          const newChosenItem = {
            ...target.data,
            quantity: 1,
            rentTerm: 1,
          };

          newReserve.instruments = [...reserve.instruments, { ...newChosenItem }];
        }
        break;

      case 'comment':
        newReserve.comment = target.data;
        break;

      case 'bonus':
        newReserve.bonus = target.data;
        break;

      case 'time':
        if (target.data <= getUnixTimestamp()) {
          errorToast('Неверное время!');
          return;
        }
       
        newReserve.time = Number(target.data);
        if (!reserve?.actID1C) {
          newReserve.isDeferrer = false;
        }
        break;

      default:
        return;
    }

    handleCloseButton();
    dispatch(Reservation.updateReserve(newReserve));
  };

  const setBonusValue = (target, value) => {
    setBonus({ ...bonus, data: { ...bonus.data, [target]: value } });
  };

  const setCommentValue = (value) => {
    setComment({ ...comment, data: value });
  };

  const setTimerValue = (value) => {
    setTimer({ ...timer, data: value });
  };

  return (
    <div className="reserve-section">
      <ActionButton text="Контрагент" onClick={() => setClient({ ...client, open: true })} icon="user-btn" />
      {client.open && (
        <ModalClient
          historyParams={{ fromRent: `/reservation${reserve.actID1C ? `/${reserve.actID1C}` : ''}` }}
          selected={client.data}
          onSubmit={setClientValueHandled}
          onClose={() => setClient({ ...client, open: false })}
        />
      )}

      {modalDeffererTime && (
        <ModalAttention
          headerText=""
          className="attention_instrument"
          onClose={onCloseDeffererTimeModal}
          onSubmit={onSubmitDeffererTimeModal}
          bodyText={
            <>
              Вы указали {moment(deffererTime * 1000).format('DD.MM.YYYY')} как дату резерва, она выходит за пределы
              стандартного резерва. Хотите сделать
              <b> отложенный резерв</b>?
            </>
          }
          submitTextButton="Да, сделать резерв отложенным"
          cancelTextButton="Нет"
        />
      )}
      {instrumentInModal && (
        <ModalAttention
          headerText=""
          className="attention_instrument"
          onClose={onCloseModal}
          onSubmit={onSubmitModal}
          bodyText={
            <>
              Товар <b>{instrumentInModal?.title}</b> в кол-ве{' '}
              <b>{instrumentInModal?.quantity_inreserve_service} шт </b>
              находится в резерве. Вы уверены, что хотите выбрать его повторно?
            </>
          }
          submitTextButton="Да, выбрать"
          cancelTextButton="Нет, отмена"
        />
      )}

      <ActionButton
        text="Номенклатура"
        onClick={() => setInstruments({ ...instruments, open: true })}
        icon="instrument-btn"
      />
      {instruments.open && (
        <ModalInstruments
          selected={instruments.data}
          defaultStock={stock || null}
          onAdd={addInstrumentValue}
          onClose={() => setInstruments({ ...instruments, open: false })}
        />
      )}

      <ActionButton
        text="Время брони"
        closeText="Добавить"
        size="-70px"
        onSubmit={() => handleChange(timer)}
        isOpen={timer.open}
        onClick={(open) => setTimer({ ...timer, open })}
        icon="timer-btn"
      >
        <div className="modal-body">
          <div className="modal-body-date">
            на
            <button className="btn-property" onClick={() => setTimerValue(getUnixTimestamp() + 3600)}>
              1 ч.
            </button>
            <button className="btn-property" onClick={() => setTimerValue(getUnixTimestamp() + 7200)}>
              2 ч.
            </button>
            <input
              type="number"
              value={Math.floor((timer.data - getUnixTimestamp()) / 3600)}
              className="input-property"
              style={{ marginLeft: '5px' }}
              onChange={({ target: { value } }) => setTimerValue(getUnixTimestamp() + Number(value) * 3600)}
            ></input>
            ч.
          </div>
          <div className="modal-body-date" style={{ margin: '10px -4px' }}>
            до
            <DatePicker
              value={new Date(timer.data * 1000)}
              onChange={(newDate) => setTimerValue(Math.floor(newDate.getTime() / 1000))}
            />
          </div>
        </div>
      </ActionButton>

      <ActionButton
        text="Комментарий"
        closeText="Добавить"
        size="-70px"
        onSubmit={() => handleChange(comment)}
        isOpen={comment.open}
        onClick={(open) => setComment({ ...comment, open })}
        icon="comment-btn"
      >
        <div className="modal-body">
          <TextAreaButton
            value={comment.data || ''}
            onChange={(value) => setCommentValue(value)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>
    </div>
  );
};

const TextAreaButton = ({ onChange, ...props }) => {
  return (
    <textarea
      className="modal-textarea"
      type="text"
      {...props}
      onChange={({ target: { value } }) => onChange(value)}
    ></textarea>
  );
};

TextAreaButton.propTypes = {
  onChange: PropTypes.func,
};

const InputButton = ({ onChange, mr, ...props }) => {
  return (
    <input
      type="text"
      {...props}
      style={mr ? { marginRight: mr } : {}}
      onChange={({ target: { value } }) => onChange(value)}
    ></input>
  );
};

InputButton.propTypes = {
  onChange: PropTypes.func,
  mr: PropTypes.string,
};

export { ActionButtonsSection };
